<template>
  <div id="totals" class="box w3">
    <div class="box-header">
      <h6 class="title">
        {{ $t("message.order.totals") }}
      </h6>
    </div>
    <div class="box-content-block">
      <div class="info-box" v-if="totalsToEdit">
        <table class="first-dark totals-table">
          <tbody>
            <tr>
              <td>
                {{ $t("message.order.shipping_preference") }}
              </td>
              <td>
                <select
                  class="default-select bordered full"
                  name="shipping-preference"
                  id="shipping-preference"
                  :disabled="!canBeEdited"
                  v-model.lazy="totalsToEdit.shippingPreference"
                >
                  <option value="">{{
                    $t("message.order.select_method")
                  }}</option>
                  <option
                    v-for="(shippingService,
                    index) in shippingServicesWithSelected"
                    :value="shippingService.code"
                    :key="`${shippingService.code}-${index}`"
                  >
                    {{ shippingService.carrierCode }} -
                    {{ shippingService.name }}
                  </option>
                </select>
                <button
                  v-if="shippingPreferenceDiffersFromCustomer && canBeEdited"
                  class="warning-action"
                  :disabled="loading"
                  @click.prevent="saveCustomerDefault('shipping_preference')"
                >
                  <i class="mdi mdi-content-save-outline"></i>
                  {{ $t("message.order.save_customer_default") }}
                  <template v-if="customerShippingPreference">
                    ({{ customerShippingPreference }})
                  </template>
                </button>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.order.price_level") }}
              </td>
              <td>
                <select
                  name="price-level"
                  class="default-select bordered full"
                  v-model.lazy="totalsToEdit.priceLevel"
                  :disabled="!userCanChangePriceLevel || !canBeEdited"
                >
                  <option
                    v-for="priceLevel in priceLevels"
                    :value="priceLevel.value"
                    :key="priceLevel.value"
                  >
                    {{ priceLevel.label }}
                  </option>
                </select>
                <button
                  v-if="priceLevelMismatch && canBeEdited"
                  class="warning-action"
                  :disabled="loading"
                  @click.prevent="matchPriceLevel"
                >
                  <i class="mdi mdi-alert-outline"></i>
                  {{ $t("message.order.match_customer_level") }} ({{
                    customerPriceLevel
                  }})
                </button>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.order.modalidad_venta") }}
              </td>
              <td>
                <select
                  name="modalidad-venta"
                  class="default-select bordered full"
                  v-model.lazy="totalsToEdit.modalidadVentaId"
                  :disabled="!canBeEdited"
                >
                  <option
                    :value="modalidadVenta.modalidadVtaId"
                    :key="modalidadVenta.modalidadVtaId"
                    v-for="modalidadVenta of modalidadesVenta"
                    >{{ modalidadVenta.codigo }}</option
                  >
                </select>
              </td>
            </tr>
            <tr>
              <td>
                {{ $t("message.order.payment_type") }}
              </td>
              <td>
                <select
                  name="payment-type"
                  class="default-select bordered full"
                  v-model.lazy="totalsToEdit.paymentType"
                  :disabled="!canBeEdited"
                >
                  <option value="">{{
                    $t("message.order.select_method")
                  }}</option>
                  <!-- Blocked: no payment type in infoauto yet, the order
                       could not be exported with it -->
                  <option
                    v-for="paymentType in paymentTypesWithSelected"
                    :key="paymentType.code"
                    :value="paymentType.code"
                    :disabled="Number(paymentType.blocked) === 1"
                  >
                    {{ paymentType.name }}
                    <template v-if="Number(paymentType.blocked) === 1">
                      ({{ $t("message.order.payment_type_blocked") }})
                    </template>
                  </option>
                </select>
                <button
                  v-if="paymentTypeDiffersFromCustomer && canBeEdited"
                  class="warning-action"
                  :disabled="loading"
                  @click.prevent="saveCustomerDefault('payment_type')"
                >
                  <i class="mdi mdi-content-save-outline"></i>
                  {{ $t("message.order.save_customer_default") }}
                  <template v-if="customerPaymentType">
                    ({{ paymentTypeName(customerPaymentType) }})
                  </template>
                </button>
              </td>
            </tr>
            <tr v-if="order.paymentFees > 0">
              <td>
                {{ $t("message.order.payment_fees") }}
              </td>
              <td>{{ order.paymentFees | currencyFormatted(true) }}</td>
            </tr>
            <!-- Montos -->
            <tr class="amount-row is-strong is-first-amount">
              <td>
                {{ $t("message.order.total_products") }}
              </td>
              <td>
                {{ order.productsTotal | currencyFormatted(true) }}
              </td>
            </tr>
            <tr class="amount-row">
              <td>
                <div>
                  {{ $t("message.order.discount") }}
                  {{ order.discountCoupon ? `(${order.discountCoupon})` : "" }}
                </div>
                <div class="row-hint">
                  <span v-if="totalsToEdit.discountType === 'percentage'"
                    >% — {{ totalsToEdit.discountPercentage }}%</span
                  >
                  <span
                    v-else-if="totalsToEdit.discountType === 'coupon'"
                    :title="order.discountCouponMessage"
                    >{{ $t("message.order.discount_coupon") }} —
                    {{
                      $tc(
                        "message.order.discount_coupon_products",
                        couponLinesCount,
                        { n: couponLinesCount }
                      )
                    }}</span
                  >
                  <span v-else>{{ $t("message.order.discount_fixed") }}</span>
                </div>
              </td>
              <td>
                <div class="amount-with-action">
                  <span>{{
                    totalsToEdit.discount | currencyFormatted(true)
                  }}</span>
                  <button
                    v-if="userCanEditDiscount && canBeEdited"
                    type="button"
                    class="inline-action"
                    @click="openDiscountModal"
                  >
                    <i class="mdi mdi-pencil-outline"></i>
                    {{ $t("message.order.edit") }}
                  </button>
                </div>
              </td>
            </tr>
            <tr class="amount-row is-core">
              <td>
                {{ $t("message.order.core") }}
              </td>
              <td>{{ totalsToEdit.core | currencyFormatted(true) }}</td>
            </tr>
            <tr class="amount-row">
              <td>
                <div class="shipping-label">
                  {{ $t("message.order.shipping") }}
                  <span
                    class="tax-chip"
                    :class="{ 'is-taxable': shippingTaxable }"
                  >
                    {{
                      shippingTaxable
                        ? $t("message.order.taxable")
                        : $t("message.order.no_taxable")
                    }}
                  </span>
                  <button
                    v-if="canBeEdited"
                    type="button"
                    class="icon-action"
                    @click="showCalculator = true"
                    :title="$t('message.order.calculate_shipping')"
                  >
                    <i class="mdi mdi-calculator-variant"></i>
                  </button>
                  <button
                    v-if="shippingCalculation"
                    type="button"
                    class="icon-action is-success"
                    @click="showCalculationModal = true"
                    :title="$t('message.order.view_details')"
                  >
                    <i class="mdi mdi-file-document-outline"></i>
                  </button>
                </div>
                <ShippingCalculator
                  v-model.lazy="showCalculator"
                  :products="order.items"
                  :postalCode="shippingAddressPostalCode"
                  :street1="order.shippingAddress?.street1 ?? ''"
                  :street2="order.shippingAddress?.street2 ?? ''"
                  :city="order.shippingAddress?.city ?? ''"
                  :country="shippingAddressCountry"
                  :state="shippingAddressState"
                  @shippingSelected="shippingSelected"
                  @close="showCalculator = false"
                />
              </td>
              <td>
                <input
                  class="regular full bordered"
                  v-if="canBeEdited"
                  type="text"
                  name="shipping-preference"
                  v-model.lazy="totalsToEdit.shippingPaid"
                />
                <template v-else>
                  {{ totalsToEdit.shippingPaid | currencyFormatted(true) }}
                </template>
                <div v-if="localDeliveryNeedsRecalc" class="inline-alert">
                  <span>
                    <i class="mdi mdi-alert-outline"></i>
                    {{ $t("message.order.local_delivery_zero_alert") }}
                  </span>
                  <button
                    v-if="canBeEdited"
                    type="button"
                    class="warning-action"
                    @click.prevent="recalculateLocalDelivery"
                  >
                    {{ $t("message.order.recalculate_local_delivery") }}
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="taxRatePercent" class="amount-row is-muted">
              <td>
                {{ $t("message.order.taxable_base") }}
              </td>
              <td>
                {{ taxableBase | currencyFormatted(true) }}
              </td>
            </tr>
            <tr class="amount-row">
              <td>
                {{ $t("message.order.tax_paid") }}
                <span v-if="taxRatePercent">({{ taxRatePercent }}%)</span>
              </td>
              <td>
                {{ totalsToEdit.tax | currencyFormatted(true) }}
              </td>
            </tr>
            <tr class="amount-row is-total">
              <td>
                {{
                  isQuote
                    ? $t("message.order.quote_total")
                    : $t("message.order.total_order")
                }}
              </td>
              <td>
                {{ order.total | currencyFormatted(true) }}
              </td>
            </tr>
            <!-- A quote is not paid: no balance -->
            <tr
              v-if="!isQuote"
              class="amount-row is-balance"
              :class="Number(order.pendingPayment) > 0 ? 'is-due' : 'is-paid'"
            >
              <td>
                {{ $t("message.order.amount_due") }}
              </td>
              <td>
                <span class="balance-pill">
                  <i
                    v-if="Number(order.pendingPayment) <= 0"
                    class="mdi mdi-check-circle-outline"
                  ></i>
                  {{ order.pendingPayment | currencyFormatted(true) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <template v-if="canBeEdited">
          <div class="w-full text-right mt-1 justify-end flex flex-row">
            <template v-if="loading">
              <BaseMiniSpinner />
              <span class="ml-2">
                {{ $t("message.order.saving") }}
              </span>
            </template>
          </div>
        </template>
      </div>
    </div>
    <ShippingCalculationModal
      :show="showCalculationModal"
      :calculation="shippingCalculation"
      @close="showCalculationModal = false"
    />

    <!-- Modal de tipo de descuento -->
    <div
      v-if="showDiscountModal"
      class="discount-overlay"
      @click.self="closeDiscountModal"
      @keydown.esc.stop="closeDiscountModal"
    >
      <div class="discount-dialog" role="dialog">
        <div class="discount-head">
          <h3>{{ $t("message.order.discount") }}</h3>
          <button type="button" class="icon-action" @click="closeDiscountModal">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <div class="discount-table-wrap">
          <table class="w-full mb-4">
            <thead>
              <tr>
                <th class="text-left text-base pb-2 pl-2">
                  {{ $t("message.order.discount_tipo") }}
                </th>
                <th class="text-center text-base pb-2">%</th>
                <th class="text-center text-base pb-2">
                  {{ $t("message.order.discount_monto_fijo") }}
                </th>
                <th class="text-center text-base pb-2">
                  {{ $t("message.order.discount_monto") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-t">
                <td class="py-3 pl-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="fixed"
                      v-model="discountModal.type"
                    />
                    <span>{{ $t("message.order.discount_fixed") }}</span>
                  </label>
                </td>
                <td class="text-center px-2 py-3">—</td>
                <td class="text-center px-2 py-3">
                  <input
                    class="regular bordered w-28 text-right"
                    type="number"
                    step="0.01"
                    min="0"
                    :disabled="discountModal.type !== 'fixed'"
                    v-model.number="discountModal.amount"
                  />
                </td>
                <td class="text-center px-2 py-3">—</td>
              </tr>
              <tr class="border-t">
                <td class="py-3 pl-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="percentage"
                      v-model="discountModal.type"
                    />
                    <span>%</span>
                  </label>
                </td>
                <td class="text-center px-2 py-3">
                  <input
                    class="regular bordered w-20 text-right"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    :disabled="discountModal.type !== 'percentage'"
                    v-model.number="discountModal.percentage"
                    @input="recalcDiscountModal"
                  />
                </td>
                <td class="text-center px-2 py-3">—</td>
                <td class="text-center px-2 py-3">
                  <input
                    class="regular bordered w-28 text-right"
                    type="number"
                    step="0.01"
                    min="0"
                    disabled
                    v-model.number="discountModal.computed"
                    readonly
                  />
                </td>
              </tr>
              <tr class="border-t">
                <td class="py-3 pl-2">
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      value="coupon"
                      v-model="discountModal.type"
                    />
                    <span>{{ $t("message.order.discount_coupon") }}</span>
                  </label>
                </td>
                <td class="px-2 py-3" colspan="2">
                  <input
                    class="regular bordered w-full uppercase"
                    type="text"
                    :placeholder="$t('message.order.discount_coupon_code')"
                    :disabled="
                      discountModal.type !== 'coupon' || applyingCoupon
                    "
                    v-model.trim="discountModal.couponCode"
                    @keydown.enter.prevent="applyCoupon"
                  />
                </td>
                <td class="text-center px-2 py-3">
                  <template v-if="hasCoupon">
                    {{ order.discount | currencyFormatted(true) }}
                  </template>
                  <template v-else>—</template>
                </td>
              </tr>
              <tr v-if="discountModal.type === 'coupon'">
                <td colspan="4" class="px-2 pb-2 text-xs text-gray-500">
                  <div
                    v-if="hasCoupon && order.discountCouponMessage"
                    class="text-green-700 font-semibold mb-1"
                  >
                    {{ order.discountCoupon }}:
                    {{ order.discountCouponMessage }}
                  </div>
                  {{ $t("message.order.discount_coupon_hint") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="discount-actions">
          <button type="button" class="dialog-btn" @click="closeDiscountModal">
            {{ $t("message.order.cancel") }}
          </button>
          <template v-if="discountModal.type === 'coupon'">
            <button
              v-if="hasCoupon"
              type="button"
              class="dialog-btn"
              :disabled="applyingCoupon"
              @click="removeCoupon"
            >
              {{ $t("message.order.discount_coupon_remove") }}
            </button>
            <button
              type="button"
              class="dialog-btn is-primary"
              :disabled="applyingCoupon || !discountModal.couponCode"
              @click="applyCoupon"
            >
              {{ $t("message.order.discount_coupon_apply") }}
            </button>
          </template>
          <button
            v-else
            type="button"
            class="dialog-btn is-primary"
            @click="applyDiscount"
          >
            {{ $t("message.order.accept") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  updateOrder,
  linkShippingCalculation,
  getOrderPaymentTypes
} from "@/api/store/OrdersService.js";
import { saveCustomerOrderDefault } from "@/api/store/CustomersService.js";
import ShippingCalculator from "@/components/order/ShippingCalculator.vue";
import { isQuoteNumber } from "@/api/store/QuotesService.js";
import ShippingCalculationModal from "@/components/order/ShippingCalculationModal.vue";

export default {
  components: {
    ShippingCalculator,
    ShippingCalculationModal
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    editing: {
      type: String,
      required: true
    },
    canBeEdited: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      totalsToEdit: null,
      // Payment types of the active store, from the server
      paymentTypes: [],
      loading: false,
      showCalculator: false,
      showCalculationModal: false,
      linkingShippingCalculation: false,
      showDiscountModal: false,
      applyingCoupon: false,
      discountModal: {
        type: "fixed",
        percentage: 0,
        amount: 0,
        computed: 0,
        couponCode: ""
      }
    };
  },
  mounted() {
    this.revertChanges();
    this.loadPaymentTypes();
  },
  watch: {
    totalsToEdit: {
      handler(val) {
        // Don't trigger save if we're linking a shipping calculation
        if (this.linkingShippingCalculation) {
          return;
        }

        if (JSON.stringify(val) !== JSON.stringify(this.originalOrder)) {
          this.saveChanges();
        }
      },
      deep: true
    }
  },
  computed: {
    // descuento de un cupón aplicado desde el admin (se recalcula con los productos)
    couponLinesCount() {
      return (this.order.items || []).filter(
        item => Number(item.couponDiscount) > 0
      ).length;
    },
    hasCoupon() {
      return (
        this.order.discountType === "coupon" && !!this.order.discountCoupon
      );
    },
    isQuote() {
      return isQuoteNumber(this.order.orderNumber);
    },
    shippingCalculation() {
      return this.order?.shippingCalculation || null;
    },
    taxRatePercent() {
      const rate = parseFloat(this.order.taxRate);
      if (!rate) return null;
      // Redondea a 2 decimales y elimina ceros sobrantes (0.07 -> 7, 0.0725 -> 7.25)
      return parseFloat((rate * 100).toFixed(2));
    },
    taxableBase() {
      const products = parseFloat(this.order.productsTotal) || 0;
      const discount = parseFloat(this.totalsToEdit.discount) || 0;
      const core = parseFloat(this.totalsToEdit.core) || 0;
      // El shipping entra en la base imponible cuando el estado lo grava,
      // y su impuesto ya viene incluido dentro de order.tax
      const shipping = this.shippingTaxable
        ? parseFloat(this.totalsToEdit.shippingPaid) || 0
        : 0;
      return parseFloat((products + core + shipping - discount).toFixed(2));
    },
    shippingTaxable() {
      return Number(this.order.shippingTaxable) === 1;
    },
    userCanChangePriceLevel() {
      return this.$store.getters["auth/currentUser"].role === "superadmin";
    },
    userCanEditDiscount() {
      return (
        this.$store.getters["auth/userHasPermission"]("order_prices_admin") ||
        this.$store.getters["auth/userHasPermission"](
          "apply_discount_limited"
        ) ||
        this.$store.getters["auth/userHasPermission"]("apply_discount_extended")
      );
    },
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    modalidadesVenta() {
      return this.$store.getters["general/getModalidadesVenta"];
    },
    originalOrder() {
      return {
        shippingPreference: this.order.shippingPreference,
        shippingPaid: this.order.shippingPaid,
        tax: this.order.tax,
        priceLevel: this.order.priceLevel,
        taxEnabled: this.order.taxEnabled,
        modalidadVentaId: this.order.modalidadVentaId,
        discount: this.order.discount,
        discountType: this.order.discountType || "fixed",
        discountPercentage: this.order.discountPercentage || 0,
        core: this.order.core,
        paymentType: this.order.paymentType,
        shippingSelectedServiceCarrier: this.order
          .shippingSelectedServiceCarrier,
        shippingSelectedServiceCode: this.order.shippingSelectedServiceCode,
        shippingSelectedServiceRate: this.order.shippingSelectedServiceRate
      };
    },
    priceLevels() {
      return this.$store.getters["general/getPriceLevels"].map(level => {
        return {
          label: `${level.codigo} (${level.localPriceLevel})`,
          value: level.localPriceLevel
        };
      });
    },
    shippingAddressPostalCode() {
      return this.order.shippingAddress?.postalCode ?? "";
    },
    shippingAddressCountry() {
      return this.order.shippingAddress?.country ?? "";
    },
    shippingAddressState() {
      return this.order.shippingAddress?.state ?? "";
    },
    paymentTypesWithSelected() {
      // Keep the order's current value visible even if the store no longer
      // offers it (e.g. credit_card set by the web checkout)
      const selected = this.totalsToEdit?.paymentType;
      if (selected && !this.paymentTypes.some(t => t.code === selected)) {
        return [...this.paymentTypes, { code: selected, name: selected }];
      }
      return this.paymentTypes;
    },
    shippingServices() {
      return this.$store.getters["general/getShippingServices"];
    },
    customerPriceLevel() {
      return this.order.customerInfoauto?.generalInfo?.priceLevel || null;
    },
    // Defaults of the order's infoauto customer (the account), from the backend
    customerShippingPreference() {
      return this.order.customerDefaults?.shippingPreference || "";
    },
    shippingPreferenceDiffersFromCustomer() {
      // Compare the saved value: the backend stores the order's persisted one
      return (
        !!this.order.customerDefaults &&
        !!this.order.shippingPreference &&
        this.order.shippingPreference !== this.customerShippingPreference
      );
    },
    customerPaymentType() {
      // Saved one, otherwise CHQ for credit clients and TCR for the rest
      return this.order.customerDefaults?.effectivePaymentType || "";
    },
    paymentTypeDiffersFromCustomer() {
      return (
        !!this.order.customerDefaults &&
        !!this.order.paymentType &&
        this.order.paymentType !== this.customerPaymentType
      );
    },
    priceLevelMismatch() {
      return (
        this.customerPriceLevel &&
        this.order.priceLevel !== this.customerPriceLevel
      );
    },
    localDeliveryNeedsRecalc() {
      const pref = this.totalsToEdit?.shippingPreference || "";
      const paid = parseFloat(this.totalsToEdit?.shippingPaid) || 0;
      return pref === "local_delivery" && paid === 0;
    },
    shippingServicesWithSelected() {
      const selectedPreference = this.totalsToEdit.shippingPreference;
      const preferenceExists = this.shippingServices.some(
        service => service.code === selectedPreference
      );
      console.log("preferenceExists", preferenceExists);
      console.log("selectedPreference", selectedPreference);
      if (!preferenceExists && selectedPreference) {
        return [
          ...this.shippingServices,
          { code: selectedPreference, name: selectedPreference }
        ];
      }
      return this.shippingServices;
    }
  },
  methods: {
    async shippingSelected(data) {
      const { option, calculationId } = data;
      console.log("Selected shipping option:", option, calculationId);

      // Set flag to prevent watcher from triggering saveChanges
      this.linkingShippingCalculation = true;

      // Update UI with selected shipping
      this.totalsToEdit.shippingPreference = option.service;
      this.totalsToEdit.shippingPaid = option.price;
      console.log(option);
      // Add new shipping service details
      this.totalsToEdit.shippingSelectedServiceCarrier = option.company;
      this.totalsToEdit.shippingSelectedServiceCode =
        option.serviceCode || option.code;
      this.totalsToEdit.shippingSelectedServiceRate = option.price;

      this.showCalculator = false;

      // Link shipping calculation to order if we have a calculation ID
      if (calculationId && this.order && this.order.orderNumber) {
        console.log(
          "Linking shipping calculation:",
          this.order.orderNumber,
          calculationId
        );

        try {
          // First, link the shipping calculation
          await linkShippingCalculation(this.order.orderNumber, calculationId, {
            service_code: option.serviceCode || option.code,
            rate: option.price,
            carrier: option.company
          });

          this.$toast.open({
            message: "Shipping calculation linked to order",
            type: "success",
            position: "top-right"
          });

          // Then, update the order totals with the shipping service details
          // Reset flag temporarily to allow saveChanges to execute
          this.linkingShippingCalculation = false;
          await this.saveChanges();
        } catch (err) {
          console.error("Failed to link shipping calculation:", err);
          this.$toast.open({
            message: "Failed to link shipping calculation",
            type: "error",
            position: "top-right"
          });
          // Reset flag on error
          this.linkingShippingCalculation = false;
        }
      } else {
        // No calculation to link, just reset flag and trigger save
        this.linkingShippingCalculation = false;
        // Trigger save to update the shipping service details
        this.saveChanges();
      }
    },
    recalculateLocalDelivery() {
      this.showCalculator = true;
    },
    async matchPriceLevel() {
      if (this.loading) return;
      this.loading = true;
      this.$emit("updating");
      try {
        const res = await updateOrder(
          this.order.orderNumber,
          "price-level-confirm",
          {},
          true
        );
        if (res.warnings) {
          res.warnings.forEach(element => {
            this.$toast.open({
              message: element,
              type: "info",
              position: "top-right",
              duration: 10000
            });
          });
        }
        this.$emit("finishedEditing", true);
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || "Error matching price level",
          type: "error",
          position: "top-right"
        });
        this.$emit("finishedEditing", false);
      } finally {
        this.loading = false;
      }
    },
    async loadPaymentTypes() {
      const store = this.$store.getters["general/getActiveStore"];
      try {
        this.paymentTypes = await getOrderPaymentTypes(store?.code || "");
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || "Error loading payment types",
          type: "error",
          position: "top-right"
        });
      }
    },
    paymentTypeName(code) {
      return this.paymentTypes.find(t => t.code === code)?.name || code;
    },
    async saveCustomerDefault(field) {
      if (this.loading) return;
      this.loading = true;
      this.$emit("updating");
      // Saves the order's persisted value, not the one being edited
      const value =
        field === "payment_type"
          ? this.order.paymentType
          : this.order.shippingPreference;
      try {
        await saveCustomerOrderDefault(this.order.orderNumber, field);
        this.$toast.open({
          message: `${this.$t(
            "message.order.save_customer_default"
          )}: ${value}`,
          type: "success",
          position: "top-right"
        });
        this.$emit("finishedEditing", true);
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || "Error saving customer default",
          type: "error",
          position: "top-right"
        });
        this.$emit("finishedEditing", false);
      } finally {
        this.loading = false;
      }
    },
    saveChanges() {
      // if (
      //   !this.totalsToEdit.shippingPreference ||
      //   this.totalsToEdit.shippingPreference == ""
      // ) {
      //   this.$toast.open({
      //     message: "Shipping service cannot be empty",
      //     type: "error",
      //     position: "top-right"
      //   });
      //   return;
      // }
      let recalculatePrices = false;
      if (this.order.priceLevel !== this.totalsToEdit.priceLevel) {
        if (
          confirm(
            `The price level changed, do you want to recalculate prices?.\n\n  New price level "${this.totalsToEdit.priceLevel}",\n\n Select "cancel" to save update without recalculate.`
          )
        ) {
          recalculatePrices = true;
        }
      }
      this.$emit("updating");
      if (!this.loading) {
        this.loading = true;
        const payload = { ...this.totalsToEdit };
        if (!this.userCanEditDiscount) {
          delete payload.discount;
          delete payload.discountType;
          delete payload.discountPercentage;
        }
        return updateOrder(
          this.order.orderNumber,
          "totals",
          payload,
          recalculatePrices
        )
          .then(res => {
            // this.$toast.open({
            //   message: "Order Updated",
            //   type: "success",
            //   position: "top-right"
            // });
            if (res.warnings) {
              res.warnings.forEach(element => {
                this.$toast.open({
                  message: element,
                  type: "info",
                  position: "top-right",
                  duration: 10000
                });
              });
            }
            this.$emit("finishedEditing", true);
          })
          .catch(e => {
            this.$toast.open({
              message: e.response.data.message,
              type: "error",
              position: "top-right"
            });
            this.$emit("finishedEditing", false);
          })
          .finally(() => {
            this.loading = false;
          });
      }
      return Promise.resolve();
    },
    revertChanges() {
      this.totalsToEdit = {
        shippingPreference: this.order.shippingPreference,
        shippingPaid: this.order.shippingPaid,
        tax: this.order.tax,
        priceLevel: this.order.priceLevel,
        taxEnabled: this.order.taxEnabled,
        modalidadVentaId: this.order.modalidadVentaId,
        discount: this.order.discount,
        discountType: this.order.discountType || "fixed",
        discountPercentage: this.order.discountPercentage || 0,
        core: this.order.core,
        paymentType: this.order.paymentType || "TCR",
        shippingSelectedServiceCarrier: this.order
          .shippingSelectedServiceCarrier,
        shippingSelectedServiceCode: this.order.shippingSelectedServiceCode,
        shippingSelectedServiceRate: this.order.shippingSelectedServiceRate
      };
      this.$emit("finishedEditing", false);
    },
    openDiscountModal() {
      this.discountModal = {
        type: this.totalsToEdit.discountType || "fixed",
        percentage: this.totalsToEdit.discountPercentage || 0,
        amount: this.totalsToEdit.discount || 0,
        computed: this.totalsToEdit.discount || 0,
        couponCode: this.hasCoupon ? this.order.discountCoupon : ""
      };
      this.showDiscountModal = true;
    },
    closeDiscountModal() {
      this.showDiscountModal = false;
    },
    recalcDiscountModal() {
      if (this.discountModal.type === "percentage") {
        const productsTotal = parseFloat(this.order.productsTotal) || 0;
        this.discountModal.computed = parseFloat(
          (productsTotal * (this.discountModal.percentage / 100)).toFixed(2)
        );
      }
    },
    applyDiscount() {
      if (this.discountModal.type === "percentage") {
        this.totalsToEdit.discountType = "percentage";
        this.totalsToEdit.discountPercentage = this.discountModal.percentage;
        this.totalsToEdit.discount = this.discountModal.computed;
      } else {
        this.totalsToEdit.discountType = "fixed";
        this.totalsToEdit.discountPercentage = 0;
        this.totalsToEdit.discount = this.discountModal.amount;
      }
      this.showDiscountModal = false;
    },
    // El cupón se aplica (o se quita) en el momento: el servidor lo valida y lo
    // recalcula con los productos; luego se recarga la orden
    async applyCoupon() {
      if (!this.discountModal.couponCode || this.applyingCoupon) return;
      await this.updateCoupon("coupon-apply", {
        code: this.discountModal.couponCode
      });
    },
    async removeCoupon() {
      await this.updateCoupon("coupon-remove", {});
    },
    async updateCoupon(section, values) {
      this.applyingCoupon = true;
      this.$emit("updating");
      try {
        const res = await updateOrder(this.order.orderNumber, section, values);
        // Un cupón que todavía no aplica a los productos queda puesto con
        // descuento 0: el servidor lo avisa en warnings
        const warnings = (res && res.warnings) || [];
        warnings.forEach(message => {
          this.$toast.open({
            message,
            type: "info",
            position: "top-right",
            duration: 10000
          });
        });
        if (warnings.length === 0) {
          this.$toast.open({
            message:
              section === "coupon-apply"
                ? `${values.code} applied`
                : "Coupon removed",
            type: "success",
            position: "top-right"
          });
        }
        this.showDiscountModal = false;
        this.$emit("finishedEditing", true);
      } catch (e) {
        this.$toast.open({
          message: e.response?.data?.message || e.message,
          type: "error",
          position: "top-right",
          duration: 8000
        });
        this.$emit("finishedEditing", false);
      } finally {
        this.applyingCoupon = false;
      }
    }
  }
};
</script>
<style lang="scss">
@import "@/assets/style/css/generic-variables.scss";
// Global: PaymentModal y PaymentOrderModal tambien usan .stripes
.stripes tr:nth-child(odd) {
  background-color: $tableBackgroundAlternative;
}
#totals td {
  padding: 5px;
}
</style>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

$border: #e5e7eb;
$muted: #6b7280;
$text: #111827;

.totals-table {
  td:last-child {
    text-align: right;
  }

  select,
  input {
    text-align: left;
  }
}

.amount-row {
  td {
    font-variant-numeric: tabular-nums;
  }

  td:last-child {
    white-space: nowrap;
  }

  &.is-first-amount td {
    border-top: 1px solid $border;
    padding-top: 12px !important;
  }

  &.is-strong td {
    font-weight: 600;
    color: $text;
  }

  &.is-muted td {
    font-size: 16px;
    color: $muted;
  }

  &.is-core td:first-child {
    box-shadow: inset 3px 0 0 #f59e0b;
    padding-left: 10px !important;
  }

  &.is-total td {
    border-top: 1px solid $border;
    padding-top: 12px !important;
    font-size: 17px;
    font-weight: 700;
    color: $text;
  }

  &.is-balance td {
    font-weight: 600;
  }
}

.balance-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-weight: 700;

  .is-due & {
    background: #fee2e2;
    color: #b91c1c;
  }

  .is-paid & {
    background: #dcfce7;
    color: #15803d;
  }
}

.row-hint {
  font-size: 16px;
  color: #6b7280;
}

.amount-with-action {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
}

.inline-action {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid $border;
  border-radius: 6px;
  background: white;
  font-size: 16px;
  font-weight: 500;
  color: $link-color-1;

  &:hover {
    border-color: $link-color-1;
  }
}

.shipping-label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.tax-chip {
  padding: 0 6px;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 16px;
  font-weight: 600;
  color: #4b5563;

  &.is-taxable {
    background: #dcfce7;
    color: #166534;
  }
}

.icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: $link-color-1;

  i {
    font-size: 18px;
  }

  &:hover {
    background: rgba(18, 110, 106, 0.08);
  }

  &.is-success {
    color: #15803d;
  }
}

.warning-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  margin-top: 6px;
  padding: 4px 8px;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  background: #fffbeb;
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
  white-space: normal;

  &:hover:not(:disabled) {
    background: #fef3c7;
  }

  &:disabled {
    opacity: 0.6;
  }
}

.inline-alert {
  margin-top: 8px;
  padding: 8px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  font-size: 16px;
  color: #991b1b;
  text-align: left;
  white-space: normal;
}

.discount-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(17, 24, 39, 0.5);
}

.discount-dialog {
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  padding: 16px 20px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 48px rgba(16, 24, 40, 0.24);
}

.discount-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: $text;
  }

  .icon-action {
    color: $muted;
  }
}

.discount-table-wrap {
  overflow-x: auto;
}

.discount-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.dialog-btn {
  height: 38px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 600;
  color: #374151;

  &:hover {
    background: #f3f4f6;
  }

  &.is-primary {
    border-color: $link-color-1;
    background: $link-color-1;
    color: white;

    &:hover {
      filter: brightness(1.1);
    }
  }
}
</style>
